import { Undo2 } from "lucide-react";
import { useEffect, useState } from "react";

const RAIO = 22;
const VOLTA = 2 * Math.PI * RAIO;

/** Barra do Desfazer (dono, 03/10/2026 — escolheu "barra embaixo, no centro"
 *  entre três imagens): fica 10 s na tela depois de cada ação do KDS, com o
 *  anel esvaziando e o número indo de 10 a 0, o que foi feito e o botão grande.
 *
 *  Cores LITERAIS, como os cartões do KDS: `orange-500` vira a cor da loja, e
 *  a barra precisa ser a mesma em toda loja, lida a dois metros.
 *
 *  Tecla "−" (teclado numérico) ou Ctrl+Z também desfaz. `alta` sobe a barra
 *  no celular do garçom, onde o canto de baixo é do botão "Ler QR de saída".
 *  Quem usa põe `key={acao.id}`: ação nova, anel e contagem do zero. */
export default function BarraDesfazer({ acao, segundos, onDesfazer, mobile = false, alta = false, comTecla = false }) {
  const total = segundos * 1000;
  // Montou atrasada (a tela redesenhou no meio): o anel entra já no ponto certo.
  const [passou] = useState(() => Math.min(total, Math.max(0, total - (acao.ate - Date.now()))));
  const [resta, setResta] = useState(segundos);

  useEffect(() => {
    const medir = () => setResta(Math.max(0, Math.ceil((acao.ate - Date.now()) / 1000)));
    medir();
    const id = setInterval(medir, 200);
    return () => clearInterval(id);
  }, [acao.ate]);

  useEffect(() => {
    const onKey = (e) => {
      const alvo = e.target;
      if (alvo && (/^(INPUT|TEXTAREA|SELECT)$/.test(alvo.tagName || "") || alvo.isContentEditable)) return;
      const menos = e.code === "NumpadSubtract" || e.code === "Minus";
      const ctrlZ = (e.ctrlKey || e.metaKey) && e.code === "KeyZ";
      if (!menos && !ctrlZ) return;
      e.preventDefault();
      onDesfazer();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onDesfazer]);

  const base = alta ? "5rem" : mobile ? "0.75rem" : "1.75rem";
  return (
    <div role="status" aria-live="polite" data-e2e="kds-desfazer"
      className={`fixed z-50 flex items-center gap-3 sm:gap-4 rounded-2xl border-2 border-[#f97316] bg-[#18181b] text-white shadow-[0_16px_48px_rgba(0,0,0,0.6)] ${
        mobile ? "left-3 right-3 py-2 pl-3 pr-2" : "left-1/2 -translate-x-1/2 py-2.5 pl-4 pr-3"}`}
      style={{ bottom: `calc(${base} + env(safe-area-inset-bottom, 0px))`, animation: "kds-desfazer-entra 180ms ease-out" }}>
      <style>{`
        @keyframes kds-desfazer-anel { from { stroke-dashoffset: 0; } to { stroke-dashoffset: ${VOLTA.toFixed(1)}; } }
        @keyframes kds-desfazer-entra { from { opacity: 0; margin-bottom: -14px; } to { opacity: 1; margin-bottom: 0; } }
      `}</style>
      <svg width={mobile ? 46 : 52} height={mobile ? 46 : 52} viewBox="0 0 52 52" className="shrink-0" aria-hidden="true">
        <circle cx="26" cy="26" r={RAIO} fill="none" stroke="rgba(255,255,255,0.18)" strokeWidth="5" />
        <circle cx="26" cy="26" r={RAIO} fill="none" stroke="#fb923c" strokeWidth="5" strokeLinecap="round"
          strokeDasharray={VOLTA.toFixed(1)} transform="rotate(-90 26 26)"
          style={{ animation: `kds-desfazer-anel ${total}ms linear forwards`, animationDelay: `-${passou}ms` }} />
        <text x="26" y="33.5" textAnchor="middle" fontSize="21" fontWeight="800" fill="#fff" data-e2e="kds-desfazer-segundos">{resta}</text>
      </svg>
      <div className={mobile ? "flex-1 min-w-0" : "min-w-[200px] max-w-[340px]"}>
        <p className="text-[11px] sm:text-xs font-semibold text-[#a1a1aa] truncate" data-e2e="kds-desfazer-titulo">{acao.titulo}</p>
        <p className="text-[15px] sm:text-[17px] font-extrabold leading-tight truncate" data-e2e="kds-desfazer-alvo">{acao.alvo}</p>
      </div>
      <button type="button" onClick={onDesfazer} data-e2e="kds-desfazer-botao"
        className={`shrink-0 flex items-center gap-2 rounded-xl bg-[#f97316] hover:bg-[#ea580c] active:scale-[0.98] text-white font-extrabold tracking-wide touch-manipulation ${
          mobile ? "h-[52px] px-4 text-[15px]" : "h-[54px] px-6 text-[17px]"}`}>
        <Undo2 className="w-5 h-5" strokeWidth={2.6} /> DESFAZER
        {comTecla && <kbd className="ml-1 rounded-md bg-white/25 px-1.5 text-sm font-bold leading-6" title="Tecla − do teclado numérico">−</kbd>}
      </button>
    </div>
  );
}
